import { createSelector, createSlice } from "@reduxjs/toolkit";
import { generateClient } from 'aws-amplify/api';
import { customerByLocationId, getCustomer, searchCustomers } from "../graphql/queries";
import { reset } from "./globalActions";
import { updateCustomer } from "../graphql/mutations";

const client = generateClient();

const moduleName = "customer";

const initialState = {
    loading: false,
    items: [],
    nextToken: null,
};

const customerSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setItems: (state, action) => {
            state.items = action.payload;
        },
        updateItem: (state, action) => {
            const idx = state.items.findIndex(i => i.id === action.payload.id);
            if (idx < 0) { state.items.push(action.payload) }
            else { state.items[idx] = action.payload }
        }
    },
});

export const { setLoading, setItems, updateItem } = customerSlice.actions;
export default customerSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectCustomers = createSelector(selectState, (state) => state.items);
export const selectCustomerById = createSelector(
    selectCustomers,
    (state, id) => id,
    (customers, id) => customers.find((c) => c.id === id)
);

export const fetchCustomers = (locationId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: customerByLocationId,
            variables: {
                locationId,
                filter: {
                    active: { eq: true }
                },
            },
        });
        dispatch(setItems(data.customerByLocationId.items));
        return data.customerByLocationId.items;
    } catch (ex) {
        console.log("failed to get customers", ex);
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchCustomer = (id, locationId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: getCustomer,
            variables: {
                id,
                locationId,
            },
        });
        dispatch(updateItem(data.getCustomer));
        return data.getCustomer;
    } finally {
        dispatch(setLoading(false));
    }
};

export const findCustomers = (search, locationId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: searchCustomers,
            variables: {
                filter: {
                    active: { eq: true },
                    locationId: { eq: locationId },
                    name: {
                        wildcard: `*${search}*`,
                    },
                },
                sort: { direction: "asc", field: "name" },
                limit: 50,
            },
        });
        dispatch(setItems(data.searchCustomers.items));
        return data.searchCustomers.items;
    } finally {
        dispatch(setLoading(false));
    }
};

export const modifyCustomer = ({ id, locationId, name }) => async (dispatch) => {
    const { data } = await client.graphql({
        query: updateCustomer,
        variables: {
            input: {
                id,
                locationId,
                name,
            }
        }
    });
    dispatch(updateItem(data.updateCustomer))
    return data.updateCustomer;
}
